import { useEffect, useId, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Carregando from "./Carregando";
import { CAMPO, Recado } from "./ui";
import { mensagemDoCatalogo } from "../lib/adminCatalogo";
import {
  convidarPorEmail,
  criarPesquisa,
  encerrarPesquisa,
  lerCursosDasPesquisas,
  lerPesquisasDoProduto,
  levarAvaliacoesAgora,
  npsEmTexto,
  type ConviteDaPesquisa as Ensaio,
  type CursoDasPesquisas,
  type PesquisaDoProduto,
} from "../lib/pesquisas";
// O MESMO molde que a Edge Function manda (000320): a prévia não pode mentir
// sobre o que chega na caixa de 34 mil pessoas.
import { HUB, montarConvite } from "../../../supabase/functions/_shared/convite-de-pesquisa";

/**
 * Admin › Feedbacks › **Pesquisas de satisfação** (000332).
 *
 * As pesquisas nasceram na página de cada produto, no Catálogo (000310 — o G1 do
 * Plano de Melhorias da Equipe de Produtos, e a decisão 2 de Victor: "NPS
 * entra"). Em 30/09 elas foram morar na aba Feedbacks, ao lado das avaliações das
 * ferramentas, a pedido: *"Unificar pesquisas de satisfação com avaliações,
 * devem morar na mesma aba do Painel. Aba 'Feedbacks'. Aí separa a visualização
 * das pesquisas e dos feedbacks de ferramentas."* O portão foi junto: as funções
 * perguntam pela aba Feedbacks desde a 000332.
 *
 * A pesquisa é de um CURSO, então a seção tem duas telas, pela mesma razão das
 * Ferramentas das avaliações (a lista e a página de uma): a lista dos cursos,
 * com quantas pesquisas cada um tem, e a página de um curso (`?curso=<slug>`),
 * onde se abre, se lê, se convida e se encerra. O curso viaja na QUERY: o link
 * de um curso é compartilhável, e o botão do navegador volta à lista.
 *
 * O que ficou no Catálogo: os INDICADORES do produto, com o NPS do produto entre
 * eles — são os números da página do produto. O indicador aponta para cá.
 *
 * ZERO regra aqui: o NPS, a janela da pesquisa, quem pode responder e quem pode
 * abrir são do banco. A tela escolhe o que mostrar primeiro — o número —, e diz
 * com letra o que o banco ainda não sabe, em vez de inventar.
 *
 * Os atributos `data-pesquisas-do-produto` e `data-pesquisa-do-produto` são do
 * tempo da página do produto, e ficaram: as provas de tela os leem, e o nome não
 * muda o que eles marcam.
 */
export default function AdminFeedbacksPesquisas({
  podeEditar,
  veCatalogo,
  secoes,
}: {
  podeEditar: boolean;
  veCatalogo: boolean;
  /** A troca entre as duas seções da aba, desenhada ao lado do título. */
  secoes?: ReactNode;
}) {
  const [busca] = useSearchParams();
  const curso = busca.get("curso");
  const [cursos, setCursos] = useState<CursoDasPesquisas[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // Abrir ou encerrar uma pesquisa muda as contas da lista: ela é relida quando
  // a página de um curso avisa que algo mudou.
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerCursosDasPesquisas();
      if (!vivo) return;
      if (r.ok) {
        setCursos(r.cursos);
        setErro(null);
      } else setErro(mensagemDoCatalogo(r));
    })();
    return () => {
      vivo = false;
    };
  }, [versao]);

  const doCurso = curso && cursos ? cursos.find((c) => c.slug === curso) : undefined;

  return (
    <div data-secao-pesquisas>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h1 className="font-display text-2xl font-bold text-titulo">Pesquisas de satisfação</h1>
        {secoes}
      </div>
      <p className="mt-1 max-w-3xl text-sm leading-relaxed text-cinza">
        O NPS de cada curso e dos professores da turma. A equipe abre a pesquisa, e quem tem o curso responde na página
        Avaliações (a página do curso avisa) ou pelo convite por e-mail, que leva um link pessoal e dispensa entrar no
        hub. A equipe vê a nota e o
        comentário, nunca o nome; na pesquisa sobre os professores, cada professor também vê, na Central de Professores,
        a nota e o comentário que recebeu. O NPS é o percentual de promotores (9 e 10) menos o de detratores (0 a 6).
      </p>

      {!podeEditar && (
        // O aviso de quem só VÊ, uma vez e no alto, e não botão a botão — o
        // desenho do Catálogo: os botões continuam, e quem recusa é o banco.
        <p role="note" className="mt-4 rounded-cf border border-borda bg-chip px-4 py-3 text-sm text-tinta" data-pesquisas-so-leitura>
          Você pode <strong>ver</strong> as pesquisas, não abrir, encerrar nem convidar: o banco recusa. Quem dá edição da
          aba Feedbacks é um master, em Admin › Permissões.
        </p>
      )}

      <div className="mt-6 flex flex-col gap-5">
        {erro && (
          <p className="surface p-6 text-sm text-erro-tinta" role="alert" data-pesquisas-erro>
            As pesquisas não vieram — {erro}
          </p>
        )}
        {!erro && !cursos && <Carregando />}
        {cursos && !curso && <ListaDosCursos cursos={cursos} />}
        {cursos && curso && !doCurso && (
          <div className="surface rounded-cf-lg p-6" data-curso-inexistente>
            <h2 className="font-display text-lg font-bold text-titulo">Este curso não existe</h2>
            <p className="mt-2 text-sm text-cinza">
              Nenhum produto tem <code>curso_slug = {curso}</code>.
            </p>
            <Link to="/admin/avaliacoes/pesquisas" className="btn-contorno mt-4 inline-flex min-h-10 items-center text-sm">
              Voltar aos cursos
            </Link>
          </div>
        )}
        {doCurso && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/admin/avaliacoes/pesquisas" className="btn-contorno inline-flex min-h-10 items-center text-sm" data-voltar-aos-cursos>
                ← Todos os cursos
              </Link>
              {veCatalogo && (
                <Link
                  to={`/admin/catalogo/produtos?produto=${encodeURIComponent(doCurso.slug)}`}
                  className="text-sm font-semibold text-link underline underline-offset-2"
                  data-pagina-do-produto
                >
                  A página do produto, com os indicadores, fica no Catálogo
                </Link>
              )}
            </div>
            <PesquisasDoCurso
              curso={doCurso.slug}
              cursoNome={doCurso.nome}
              faculdade={doCurso.faculdade}
              aoMudar={() => setVersao((v) => v + 1)}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ── A lista dos cursos ─────────────────────────────────────────────────────

function ListaDosCursos({ cursos }: { cursos: CursoDasPesquisas[] }) {
  const idTitulo = useId();
  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby={idTitulo} data-cursos-das-pesquisas={cursos.length}>
      <h2 id={idTitulo} className="font-display text-lg font-bold text-titulo">
        Os cursos
      </h2>
      <p className="mt-1 text-sm text-cinza">Escolha o curso para abrir uma pesquisa ou para ler o resultado das que já existem.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borda text-left text-xs text-cinza uppercase">
              <th className="py-2 pr-4 font-semibold">Curso</th>
              <th className="py-2 pr-4 text-right font-semibold">Pesquisas</th>
              <th className="py-2 pr-4 text-right font-semibold">Abertas</th>
              <th className="py-2 text-right font-semibold">Respostas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {cursos.map((c) => (
              <tr key={c.slug} data-curso-das-pesquisas={c.slug}>
                <td className="py-2.5 pr-4">
                  <Link
                    to={`/admin/avaliacoes/pesquisas?curso=${encodeURIComponent(c.slug)}`}
                    className="font-semibold text-link underline-offset-2 hover:underline"
                    data-abrir-curso={c.slug}
                  >
                    {c.nome}
                  </Link>
                  <span className="ml-2 text-xs text-cinza">{c.faculdade ? "faculdade" : "curso livre"}</span>
                </td>
                <td className={`py-2.5 pr-4 text-right tabular-nums ${c.pesquisas ? "font-semibold text-tinta" : "text-cinza"}`} data-pesquisas={c.pesquisas}>
                  {c.pesquisas}
                </td>
                <td className={`py-2.5 pr-4 text-right tabular-nums ${c.abertas ? "font-semibold text-tinta" : "text-cinza"}`} data-abertas={c.abertas}>
                  {c.abertas}
                </td>
                <td className={`py-2.5 text-right tabular-nums ${c.respostas ? "font-semibold text-tinta" : "text-cinza"}`} data-respostas={c.respostas}>
                  {c.respostas.toLocaleString("pt-BR")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── As pesquisas de UM curso ───────────────────────────────────────────────

const dia = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

const PERGUNTA_DO_CURSO = "Em uma escala de 0 a 10, o quanto você recomendaria este curso a um amigo ou colega?";
const PERGUNTA_DO_PROFESSOR = "Em uma escala de 0 a 10, o quanto você recomendaria as aulas deste professor a um colega?";

function PesquisasDoCurso({
  curso,
  cursoNome,
  faculdade,
  aoMudar,
}: {
  curso: string;
  /** O nome que o aluno vê — é o que a prévia do e-mail mostra. */
  cursoNome: string;
  faculdade: boolean;
  aoMudar?: () => void;
}) {
  const idTitulo = useId();
  const [pesquisas, setPesquisas] = useState<PesquisaDoProduto[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const [abrindo, setAbrindo] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerPesquisasDoProduto(curso);
      if (!vivo) return;
      if (r.ok) {
        setPesquisas(r.pesquisas);
        setErro(null);
      } else setErro(mensagemDoCatalogo(r));
    })();
    return () => {
      vivo = false;
    };
  }, [curso, versao]);

  function mudou(texto: string, ok: boolean) {
    setRecado({ tipo: ok ? "ok" : "erro", texto });
    if (ok) {
      setVersao((v) => v + 1);
      aoMudar?.();
    }
  }

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby={idTitulo} data-pesquisas-do-produto data-curso={curso}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h2 id={idTitulo} className="font-display text-xl font-bold text-titulo">
            {cursoNome}
          </h2>
          <p className="mt-1 text-sm text-cinza">
            {faculdade
              ? "Curso de faculdade: a pesquisa pode ser sobre o curso (é o NPS do produto) ou sobre os professores da turma de cada aluno."
              : "Curso livre: a pesquisa é sobre o curso, e é ela o NPS do produto."}
          </p>
        </div>
        {!abrindo && (
          <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setAbrindo(true)} data-abrir-pesquisa>
            + Abrir pesquisa
          </button>
        )}
      </div>

      {abrindo && (
        <FormularioDePesquisa
          curso={curso}
          faculdade={faculdade}
          aoFechar={() => setAbrindo(false)}
          aoAbrir={(texto) => {
            setAbrindo(false);
            mudou(texto, true);
          }}
        />
      )}

      {recado && <div data-recado-pesquisas><Recado tipo={recado.tipo} texto={recado.texto} /></div>}
      {erro && <p className="mt-3 text-sm text-erro-tinta" role="alert">{erro}</p>}
      {!pesquisas && !erro && <p className="mt-3 text-sm text-cinza">Carregando…</p>}
      {pesquisas && pesquisas.length === 0 && (
        <p className="mt-4 text-sm text-cinza" data-sem-pesquisas>
          Nenhuma pesquisa foi aberta para este curso.
        </p>
      )}
      {pesquisas && pesquisas.length > 0 && (
        <ul className="mt-4 flex flex-col gap-4">
          {pesquisas.map((p) => (
            <CartaoDaPesquisa
              key={p.id}
              p={p}
              cursoNome={cursoNome}
              aoMudar={(texto, ok) => mudou(texto, ok)}
              aoAtualizar={() => setVersao((v) => v + 1)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FormularioDePesquisa({
  curso,
  faculdade,
  aoFechar,
  aoAbrir,
}: {
  curso: string;
  faculdade: boolean;
  aoFechar: () => void;
  aoAbrir: (texto: string) => void;
}) {
  const id = useId();
  const [alvo, setAlvo] = useState<"curso" | "professor">("curso");
  const [titulo, setTitulo] = useState("");
  const [pergunta, setPergunta] = useState(PERGUNTA_DO_CURSO);
  const [fim, setFim] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function escolherAlvo(a: "curso" | "professor") {
    setAlvo(a);
    // A pergunta-padrão acompanha o alvo, mas só enquanto ninguém a escreveu.
    if (pergunta === PERGUNTA_DO_CURSO || pergunta === PERGUNTA_DO_PROFESSOR) {
      setPergunta(a === "curso" ? PERGUNTA_DO_CURSO : PERGUNTA_DO_PROFESSOR);
    }
  }

  async function abrir() {
    setEnviando(true);
    setErro(null);
    // O fim é o FIM DO DIA escolhido, em São Paulo (sem horário de verão desde
    // 2019, então o deslocamento é fixo). Vazio = até alguém encerrar.
    const r = await criarPesquisa({ curso, alvo, titulo, pergunta, fecha_em: fim ? `${fim}T23:59:59-03:00` : null });
    setEnviando(false);
    if (r.ok) aoAbrir(mensagemDoCatalogo(r));
    else setErro(mensagemDoCatalogo(r));
  }

  return (
    <div className="mt-4 rounded-xl border border-borda p-4" data-formulario-pesquisa>
      <div role="radiogroup" aria-labelledby={`${id}-alvo`} className="flex flex-wrap gap-4 text-sm">
        <span id={`${id}-alvo`} className="w-full text-xs font-bold tracking-widest text-cinza uppercase">
          Sobre o quê
        </span>
        <label className="flex items-center gap-2">
          <input type="radio" name={`${id}-alvo`} checked={alvo === "curso"} onChange={() => escolherAlvo("curso")} data-alvo="curso" />
          O curso (é o NPS do produto)
        </label>
        {faculdade && (
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name={`${id}-alvo`}
              checked={alvo === "professor"}
              onChange={() => escolherAlvo("professor")}
              data-alvo="professor"
            />
            Os professores da turma do aluno (um NPS por professor)
          </label>
        )}
      </div>
      <label htmlFor={`${id}-titulo`} className="mt-4 block text-xs font-bold tracking-widest text-cinza uppercase">
        Título
      </label>
      <input
        id={`${id}-titulo`}
        className={`${CAMPO} mt-1`}
        value={titulo}
        onChange={(e) => setTitulo(e.target.value)}
        maxLength={120}
        placeholder="Ex.: Lançamento de setembro"
        data-titulo-pesquisa
      />
      <label htmlFor={`${id}-pergunta`} className="mt-3 block text-xs font-bold tracking-widest text-cinza uppercase">
        Pergunta
      </label>
      <textarea
        id={`${id}-pergunta`}
        className={`${CAMPO} mt-1`}
        rows={2}
        value={pergunta}
        onChange={(e) => setPergunta(e.target.value)}
        maxLength={300}
        data-pergunta-pesquisa
      />
      <label htmlFor={`${id}-fim`} className="mt-3 block text-xs font-bold tracking-widest text-cinza uppercase">
        Aberta até <span className="font-normal normal-case tracking-normal">(opcional — vazio: até alguém encerrar)</span>
      </label>
      <input id={`${id}-fim`} type="date" className={`${CAMPO} mt-1 max-w-xs`} value={fim} onChange={(e) => setFim(e.target.value)} data-fim-pesquisa />
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className="pill-cta" disabled={enviando} onClick={() => void abrir()} data-confirmar-pesquisa>
          {enviando ? "Abrindo…" : "Abrir pesquisa"}
        </button>
        <button type="button" className="btn-contorno min-h-10 text-sm" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
      {erro && <Recado tipo="erro" texto={erro} />}
    </div>
  );
}

function CartaoDaPesquisa({
  p,
  cursoNome,
  aoMudar,
  aoAtualizar,
}: {
  p: PesquisaDoProduto;
  cursoNome: string;
  aoMudar: (texto: string, ok: boolean) => void;
  aoAtualizar: () => void;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [levando, setLevando] = useState(false);
  const maior = Math.max(1, ...p.por_nota);

  async function encerrar() {
    setEnviando(true);
    const r = await encerrarPesquisa(p.id);
    setEnviando(false);
    setConfirmando(false);
    aoMudar(mensagemDoCatalogo(r), r.ok);
  }

  async function levar() {
    setLevando(true);
    const r = await levarAvaliacoesAgora();
    setLevando(false);
    aoMudar(r.detalhe ?? (r.ok ? "Feito." : "Não foi possível levar agora."), r.ok);
  }

  return (
    <li className="rounded-xl border border-borda p-4" data-pesquisa-do-produto={p.titulo}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-tinta">
          {p.titulo}{" "}
          <span className="ml-1 rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta" data-situacao={p.aberta ? "aberta" : "encerrada"}>
            {p.aberta ? "aberta" : "encerrada"}
          </span>
        </p>
        <p className="text-xs text-cinza">
          {p.alvo === "curso" ? "sobre o curso" : "sobre os professores"} · aberta em {dia(p.abre_em)} por {p.criada_por}
          {p.fechada_em
            ? ` · encerrada em ${dia(p.fechada_em)}${p.fechada_por ? ` por ${p.fechada_por}` : ""}`
            : p.fecha_em
              ? ` · até ${dia(p.fecha_em)}`
              : ""}
        </p>
      </div>
      <p className="mt-1 text-sm text-cinza">{p.pergunta}</p>

      <div className="mt-3 flex flex-wrap items-end gap-6">
        <div>
          <p className="text-xs font-bold tracking-widest text-cinza uppercase">NPS</p>
          <p className="font-display text-3xl font-bold text-titulo" data-nps={p.nps ?? ""}>
            {npsEmTexto(p.nps)}
          </p>
        </div>
        <p className="text-sm text-tinta" data-contagem={p.respostas}>
          {p.respostas} resposta(s) · {p.promotores} promotor(es) · {p.neutros} neutro(s) · {p.detratores} detrator(es)
        </p>
      </div>

      {p.respostas > 0 && (
        // A distribuição por nota: a barra é proporção, e o NÚMERO de cada nota
        // está escrito — para quem não enxerga a barra, e para quem precisa do
        // número. As três cores são as das três categorias do NPS.
        <ol className="mt-3 flex items-end gap-1" aria-label="Respostas por nota" data-por-nota={p.por_nota.join(",")}>
          {p.por_nota.map((n, nota) => (
            <li key={nota} className="flex w-8 flex-col items-center gap-0.5" aria-label={`nota ${nota}: ${n} resposta(s)`}>
              <span className="text-[10px] text-cinza" aria-hidden="true">
                {n}
              </span>
              <span
                aria-hidden="true"
                className={`w-full rounded-t ${nota >= 9 ? "bg-aviso-tinta" : nota >= 7 ? "bg-alerta-tinta" : "bg-erro-tinta"}`}
                style={{ height: `${Math.max(2, Math.round((n / maior) * 48))}px` }}
              />
              <span className="text-xs font-semibold text-tinta" aria-hidden="true">
                {nota}
              </span>
            </li>
          ))}
        </ol>
      )}

      {p.por_professor && p.por_professor.length > 0 && (
        <table className="mt-4 w-full text-sm" data-por-professor>
          <thead>
            <tr className="text-left text-xs text-cinza">
              <th className="py-1 font-semibold">Professor</th>
              <th className="py-1 font-semibold">Respostas</th>
              <th className="py-1 font-semibold">NPS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {p.por_professor.map((d) => (
              <tr key={d.docente_id} data-professor={d.nome}>
                <td className="py-1.5 text-tinta">{d.nome}</td>
                <td className="py-1.5 text-tinta">{d.respostas}</td>
                <td className="py-1.5 font-semibold text-titulo">{npsEmTexto(d.nps)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {p.central && p.respostas > 0 && (
        // O espelho na Central (000318): o que falha em silêncio é o que ninguém
        // corrige, então o estado fica À VISTA — quantas estão lá, quantas faltam
        // e, se houver, o último erro, com a frase da porta.
        <div className="mt-3 text-sm" data-central={`${p.central.levadas}/${p.central.levadas + p.central.faltam}`}>
          <p className="text-tinta">
            Na Central de Professores: {p.central.levadas} de {p.central.levadas + p.central.faltam}{" "}
            {p.central.levadas + p.central.faltam === 1 ? "resposta" : "respostas"}
            {p.central.faltam > 0
              ? ` — ${p.central.faltam === 1 ? "falta 1" : `faltam ${p.central.faltam}`}; a Central as pede sozinha a cada 15 minutos.`
              : " — todas lá, e cada professor vê as dele."}
          </p>
          {p.central.com_erro > 0 && (
            <p className="mt-1 text-erro-tinta" data-central-erro>
              {p.central.com_erro === 1 ? "1 não chegou" : `${p.central.com_erro} não chegaram`}
              {p.central.ultimo_erro ? `: ${p.central.ultimo_erro}` : ""}.
            </p>
          )}
          {p.central.faltam > 0 && (
            <button type="button" className="btn-contorno mt-2 min-h-10 text-sm" disabled={levando} onClick={() => void levar()} data-levar-agora>
              {levando ? "Levando…" : "Levar agora"}
            </button>
          )}
        </div>
      )}

      <ConviteDaPesquisa p={p} cursoNome={cursoNome} aoMudar={aoMudar} aoAtualizar={aoAtualizar} />

      {p.com_comentario > 0 && (
        <details className="mt-3" data-comentarios={p.com_comentario}>
          <summary className="cursor-pointer text-sm font-semibold text-link">
            {p.com_comentario} comentário(s){p.com_comentario > p.comentarios.length ? ` — os ${p.comentarios.length} mais novos` : ""}
          </summary>
          <ul className="mt-2 divide-y divide-borda text-sm">
            {p.comentarios.map((c, i) => (
              <li key={i} className="py-2">
                <span className="font-semibold text-tinta">nota {c.nota}</span>
                {c.professor && <span className="text-cinza"> · {c.professor}</span>}
                <span className="text-cinza"> · {dia(c.em)}</span>
                <p className="mt-0.5 whitespace-pre-line text-tinta">{c.comentario}</p>
              </li>
            ))}
          </ul>
        </details>
      )}

      {p.aberta && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {!confirmando ? (
            <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setConfirmando(true)} data-encerrar>
              Encerrar pesquisa
            </button>
          ) : (
            <>
              <span className="text-sm text-tinta">O aluno deixa de ver a pesquisa; as respostas ficam aqui.</span>
              <button type="button" className="pill-cta" disabled={enviando} onClick={() => void encerrar()} data-confirmar-encerrar>
                {enviando ? "Encerrando…" : "Encerrar agora"}
              </button>
              <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setConfirmando(false)}>
                Cancelar
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

// ── O convite por e-mail (000320) ──────────────────────────────────────────

/** Por que um convite não saiu — o motivo gravado na hora de mandar. */
const POR_QUE_NAO_SAIU: Record<string, string> = {
  pesquisa_encerrada: "a pesquisa foi encerrada antes do envio",
  descadastrou: "pediram para não receber pesquisas",
  sem_acesso: "perderam o acesso ao curso",
  sem_professor: "ficaram sem professor da turma para avaliar",
  ja_respondeu: "já tinham respondido",
  endereco_de_teste: "endereço de teste",
};

/** "1 aluno" e "34.519 alunos": a frase concorda com o número (a cicatriz do "cerca de 1 páginas"). */
function alunos(n: number): string {
  return `${n.toLocaleString("pt-BR")} ${n === 1 ? "aluno" : "alunos"}`;
}

/**
 * O convite de UMA pesquisa: o andamento da fila, o ato de convidar e a prévia
 * do e-mail.
 *
 * Convidar é em DOIS passos, e não é cerimônia: o e-mail não volta atrás e pode
 * chegar a 34 mil pessoas (a Escola, medido em 28/09). O primeiro clique ENSAIA
 * — o banco conta quem vai receber e por que os outros não, sem gravar nada —, e
 * o segundo manda, com o número escrito no botão. Quem decide o público e a
 * permissão é o banco; esta tela só mostra o que ele respondeu.
 */
function ConviteDaPesquisa({
  p,
  cursoNome,
  aoMudar,
  aoAtualizar,
}: {
  p: PesquisaDoProduto;
  cursoNome: string;
  aoMudar: (texto: string, ok: boolean) => void;
  aoAtualizar: () => void;
}) {
  const [ensaio, setEnsaio] = useState<Ensaio | null>(null);
  const [ensaiando, setEnsaiando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [previa, setPrevia] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const cv = p.convites;

  async function ensaiar() {
    setEnsaiando(true);
    setErro(null);
    const r = await convidarPorEmail(p.id, true);
    setEnsaiando(false);
    if (r.ok) setEnsaio(r);
    else setErro(mensagemDoCatalogo(r));
  }

  async function enviar() {
    setEnviando(true);
    const r = await convidarPorEmail(p.id, false);
    setEnviando(false);
    setEnsaio(null);
    aoMudar(mensagemDoCatalogo(r), r.ok);
  }

  // A prévia é o e-mail de verdade, montado com um token de mentira: os links
  // dela não abrem nada, e o `sandbox` do iframe tira deles o poder de navegar.
  const email = previa
    ? montarConvite(
        { token: "0".repeat(32), nome: "Maria", cursoNome, alvo: p.alvo, pergunta: p.pergunta, fechaEm: p.fecha_em },
        { hub: HUB, funcoes: "" },
      )
    : null;

  const fora = ensaio
    ? ([
        ["ja_convidados", "já receberam o convite"],
        ["descadastrados", "pediram para não receber pesquisas"],
        ["sem_email", "não têm e-mail válido"],
        ["sem_professor", "não têm professor da turma para avaliar"],
        ["ja_responderam", "já responderam"],
      ] as const)
        .map(([chave, rotulo]) => [Number(ensaio[chave] ?? 0), rotulo] as const)
        .filter(([n]) => n > 0)
    : [];

  return (
    <div className="mt-4 rounded-xl border border-borda p-4" data-convite={cv.total}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-tinta">Convite por e-mail</p>
        {cv.total > 0 && (
          <button type="button" className="text-sm font-semibold text-link underline" onClick={aoAtualizar} data-atualizar-convite>
            Atualizar os números
          </button>
        )}
      </div>

      {cv.total === 0 ? (
        <p className="mt-1 text-sm text-cinza" data-sem-convite>
          Ninguém foi convidado por e-mail ainda.
        </p>
      ) : (
        <>
          <p
            className="mt-1 text-sm text-tinta"
            data-convite-numeros={`${cv.enviados}/${cv.total}/${cv.na_fila}/${cv.falharam}/${cv.dispensados}/${cv.responderam}`}
          >
            {cv.enviados.toLocaleString("pt-BR")} de {alunos(cv.total)} com o e-mail enviado
            {cv.na_fila > 0 && ` · ${cv.na_fila.toLocaleString("pt-BR")} na fila`}
            {cv.falharam > 0 && ` · ${cv.falharam.toLocaleString("pt-BR")} com o endereço recusado`}
            {cv.dispensados > 0 && ` · ${cv.dispensados.toLocaleString("pt-BR")} não saíram`}
            {` · ${cv.responderam.toLocaleString("pt-BR")} responderam`}
            {cv.enviados > 0 && ` (${Math.round((cv.responderam / cv.enviados) * 100)}% dos enviados)`}.
          </p>
          {cv.na_fila > 0 && (
            <p className="mt-1 text-xs text-cinza">O envio sai aos poucos, um lote por minuto — atualize para ver o andamento.</p>
          )}
          {cv.dispensados > 0 && (
            <p className="mt-1 text-xs text-cinza" data-dispensados>
              Não saíram porque:{" "}
              {Object.entries(cv.dispensados_por)
                .map(([porque, n]) => `${n.toLocaleString("pt-BR")} ${POR_QUE_NAO_SAIU[porque] ?? porque}`)
                .join("; ")}
              .
            </p>
          )}
          {cv.ultimo_erro && (cv.na_fila > 0 || cv.falharam > 0) && (
            <p className="mt-1 text-sm text-erro-tinta" data-convite-erro>
              Último erro do envio: {cv.ultimo_erro}
            </p>
          )}
        </>
      )}

      {p.aberta && !ensaio && (
        <button type="button" className="btn-contorno mt-3 min-h-10 text-sm" disabled={ensaiando} onClick={() => void ensaiar()} data-convidar>
          {ensaiando ? "Contando…" : cv.total > 0 ? "Convidar quem ainda não recebeu…" : "Convidar por e-mail…"}
        </button>
      )}

      {ensaio && (
        <div className="mt-3 rounded-xl bg-alerta-fundo p-4 text-sm text-alerta-tinta" data-confirmar-convite={ensaio.convidar ?? 0}>
          {(ensaio.convidar ?? 0) === 0 ? (
            <p>{mensagemDoCatalogo(ensaio)}</p>
          ) : (
            <p>
              O convite vai para <strong>{alunos(ensaio.convidar ?? 0)}</strong>, de {alunos(ensaio.publico ?? 0)} que
              podem responder. O e-mail sai em nome do Contabilidade Facilitada e <strong>não volta atrás</strong>.
            </p>
          )}
          {fora.length > 0 && (
            <p className="mt-1" data-fora-do-convite>
              Ficam de fora: {fora.map(([n, rotulo]) => `${n.toLocaleString("pt-BR")} ${rotulo}`).join("; ")}.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-3">
            {(ensaio.convidar ?? 0) > 0 && (
              <button type="button" className="pill-cta" disabled={enviando} onClick={() => void enviar()} data-confirmar-envio>
                {enviando ? "Enfileirando…" : `Enviar a ${alunos(ensaio.convidar ?? 0)}`}
              </button>
            )}
            <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setEnsaio(null)} data-cancelar-convite>
              Cancelar
            </button>
          </div>
        </div>
      )}
      {erro && <Recado tipo="erro" texto={erro} />}

      <button type="button" className="mt-3 block text-sm font-semibold text-link underline" onClick={() => setPrevia((v) => !v)} data-previa-botao>
        {previa ? "Esconder o e-mail" : "Ver o e-mail que o aluno recebe"}
      </button>
      {email && (
        <div className="mt-2" data-previa>
          <p className="text-xs text-cinza">
            Assunto: <span className="font-semibold text-tinta">{email.assunto}</span> — o nome e o link mudam para
            cada aluno.
          </p>
          {/* Cor FIXA dentro: o e-mail é papel (a doutrina do documento
              imprimível), e sai igual no tema claro e no escuro de quem vê. */}
          <iframe
            title="Prévia do e-mail do convite"
            sandbox=""
            srcDoc={email.html}
            className="mt-2 h-[760px] w-full rounded-xl border border-borda bg-white"
          />
        </div>
      )}
    </div>
  );
}
